import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, animate } from "framer-motion";
import { toast } from "sonner";

const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };
import { Utensils, Crown, Flame, Map, Leaf, Truck, Star, Lock, Brain, Share2, Gift, ChevronDown, Smartphone, RotateCcw } from "lucide-react";
import { api, USER_ID, money, track } from "../api";
import { SectionTitle, DishCard, rewardToast } from "../components";
import { StampSeal } from "../StampSeal";
import { shareStamp } from "../shareStamp";
import { ModuleGate, useFlags, flagOn } from "../flags";
import { useSeo } from "../seo";
import { useT } from "../i18n";
import { shareBadge } from "../share";

const ICONS = { utensils: Utensils, crown: Crown, flame: Flame, map: Map, leaf: Leaf, truck: Truck, star: Star };

const CountUp = ({ value }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const c = animate(0, value, { duration: 0.9, ease: "easeOut", onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [value]);
  return <>{n}</>;
};

const Fade = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay, ease: "easeOut" }}>
    {children}
  </motion.div>
);

const RewardWall = ({ onRedeemed }) => {
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [issued, setIssued] = useState(null);
  const load = useCallback(() => api.get("/rewards", { params: { user_id: USER_ID } }).then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const redeem = async () => {
    const r = confirm;
    try {
      const res = await api.post(`/rewards/${r.slug}/redeem`, { user_id: USER_ID });
      setConfirm(null);
      setIssued(res.data);
      load();
      onRedeemed();
      window.dispatchEvent(new CustomEvent("xp-mascot", { detail: { type: "reward", name: r.name } }));
    } catch {
      setConfirm(null);
      toast("Not enough Urban Points yet — keep exploring!");
    }
  };

  // points display hidden from customers (June 2026) — redemption data and logic kept
  if (data) return null;
  return (
    <div className="mt-6" data-testid="section-rewards">
      <SectionTitle kicker="Real rewards — redeem at your table" title="Reward wall" testId="section-rewards-title" />
      {data.my_redemptions.length > 0 && (
        <div data-testid="my-redemptions" className="mb-3 rounded-2xl border border-[#D4AF37]/60 bg-[#D4AF37]/10 p-4">
          <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold mb-1">READY TO CLAIM — SHOW YOUR SERVER</p>
          {data.my_redemptions.map((m) => (
            <div key={m.code} data-testid={`my-redemption-${m.code}`} className="flex justify-between items-center py-1.5">
              <span className="text-xs font-semibold">{m.reward_name}</span>
              <span className="font-display text-lg font-bold text-[#D4AF37] tracking-[0.2em]">{m.code}</span>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-3">
        {data.rewards.map((r) => (
          <div key={r.slug} data-testid={`reward-${r.slug}`}
            className={`rounded-2xl border p-4 flex items-center gap-3 ${r.affordable ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.06] shadow-[0_0_20px_rgba(212,175,55,0.1)]" : "border-white/10 bg-[#121212]"}`}>
            {r.image ? (
              <img src={r.image} alt={r.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-[#1F1F1F] border border-[#D4AF37]/30 flex items-center justify-center shrink-0"><Gift size={20} className="text-[#D4AF37]" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight">{r.name}</p>
              <p className="text-[10px] text-[#D4AF37] font-black tracking-widest mt-0.5">{r.points_cost} PTS{r.value ? ` · ${money(r.value)} VALUE` : ""}</p>
              {r.brand && (
                <span data-testid={`reward-brand-${r.slug}`} className="inline-block mt-1 text-[8px] font-black tracking-widest rounded-full px-2 py-0.5 bg-white/10 text-white/60">
                  AT {(r.brand_label || r.brand).toUpperCase()}
                </span>
              )}
              {!r.affordable && (
                <div className="mt-1.5 h-1 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#D4AF37]/70 rounded-full" style={{ width: `${Math.min(100, Math.round((data.points / r.points_cost) * 100))}%` }} />
                </div>
              )}
            </div>
            {r.affordable ? (
              <button data-testid={`redeem-${r.slug}`} onClick={() => setConfirm(r)}
                className="shrink-0 bg-[#D4AF37] text-black text-[10px] font-bold rounded-full px-4 py-2.5 tracking-widest hover:bg-[#F0C742] transition-colors">
                REDEEM
              </button>
            ) : (
              <span data-testid={`reward-away-${r.slug}`} className="shrink-0 text-[9px] font-bold tracking-widest text-white/40 text-right leading-snug">{r.pts_away} PTS<br />AWAY</span>
            )}
          </div>
        ))}
      </div>
      {confirm && (
        <div data-testid="redeem-confirm" className="fixed inset-0 z-[80] bg-black/75 flex items-center justify-center p-6" onClick={() => setConfirm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-3xl bg-[#141414] border border-[#D4AF37]/40 p-6 text-center">
            <Gift size={24} className="mx-auto text-[#D4AF37]" />
            <h3 className="font-display text-2xl font-bold mt-2">{confirm.name}</h3>
            <p className="text-sm text-white/60 mt-2">Spend {confirm.points_cost} Urban Points? Your server will honour the code shown on your screen.</p>
            <button data-testid="redeem-confirm-btn" onClick={redeem}
              className="w-full mt-5 bg-[#D4AF37] text-black font-bold rounded-full py-3 text-xs tracking-widest hover:bg-[#F0C742] transition-colors">
              CONFIRM — {confirm.points_cost} PTS
            </button>
            <button data-testid="redeem-cancel-btn" onClick={() => setConfirm(null)}
              className="w-full mt-2 border border-white/20 text-white/60 font-bold rounded-full py-3 text-xs tracking-widest">
              CANCEL
            </button>
          </div>
        </div>
      )}
      {issued && (
        <div data-testid="redeem-issued" className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-6" onClick={() => setIssued(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-3xl bg-[#141414] border border-[#D4AF37]/60 p-6 text-center shadow-[0_0_40px_rgba(212,175,55,0.25)]">
            <p className="text-[10px] tracking-[0.35em] text-[#D4AF37] font-bold">REWARD UNLOCKED</p>
            <h3 className="font-display text-2xl font-bold mt-2">{issued.reward_name}</h3>
            <p data-testid="redeem-code" className="font-display text-5xl font-bold text-[#D4AF37] tracking-[0.25em] mt-4">{issued.code}</p>
            <p className="text-sm text-white/60 mt-3">Show this code to your server — they'll take care of the rest.</p>
            <button data-testid="redeem-done-btn" onClick={() => setIssued(null)}
              className="w-full mt-5 bg-[#D4AF37] text-black font-bold rounded-full py-3 text-xs tracking-widest">
              DONE
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Passport() {
  return (
    <ModuleGate flag="passport">
      <PassportInner />
    </ModuleGate>
  );
}

const PhoneLink = ({ user, onLinked }) => {
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const linked = user?.phone;
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/passport/link-phone", { user_id: USER_ID, phone });
      toast.success("Phone linked — points from every counter order land here automatically", { style: GOLD_TOAST });
      setEditing(false);
      setPhone("");
      onLinked?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not link that number");
    }
    setBusy(false);
  };
  return (
    <div data-testid="phone-link-card" className="mt-4 rounded-2xl bg-[#121212] border border-white/10 p-4">
      <div className="flex items-center gap-3">
        <Smartphone size={18} className="text-[#D4AF37] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold tracking-widest">{linked ? "PHONE LINKED" : "LINK YOUR PHONE"}</p>
          <p className="text-[11px] text-white/50 mt-0.5">
            {linked
              ? <span data-testid="phone-linked-value">{`${linked} — counter orders at any Urban brand earn points here.`}</span>
              : "One passport across all five Urban brands — link your phone and every counter order earns points automatically."}
          </p>
        </div>
        {!editing && (
          <button data-testid="phone-link-open" onClick={() => setEditing(true)}
            className="shrink-0 text-[9px] font-black tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-3 py-1.5">
            {linked ? "CHANGE" : "LINK"}
          </button>
        )}
      </div>
      {editing && (
        <div className="flex gap-2 mt-3">
          <input data-testid="phone-link-input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="(312) 555-0123"
            className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/60" />
          <button data-testid="phone-link-save" onClick={save} disabled={busy || phone.replace(/\D/g, "").length < 10}
            className="shrink-0 bg-[#D4AF37] text-black text-[10px] font-black rounded-full px-4 tracking-widest disabled:opacity-50">
            {busy ? "SAVING…" : "SAVE"}
          </button>
          <button data-testid="phone-link-cancel" onClick={() => setEditing(false)}
            className="shrink-0 text-[10px] font-bold tracking-widest text-white/50">CANCEL</button>
        </div>
      )}
    </div>
  );
};

const StoryStamps = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/story-stamps", { params: { user_id: USER_ID } }).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || data.total === 0) return null;
  return (
    <div className="mt-8" data-testid="story-stamps">
      <SectionTitle kicker={data.complete ? "Passport stamps · COMPLETE" : `Passport stamps · ${data.collected + (data.brands_collected || 0)} of ${data.total + (data.brands_total || 0)} collected`} title="Passport stamps" testId="section-story-stamps" />
      {data.complete && (
        <div data-testid="stampset-complete" className="mb-3 rounded-2xl border border-[#D4AF37] bg-[#D4AF37]/10 px-4 py-3">
          <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black">PASSPORT COMPLETE!</p>
          <p className="text-xs text-white/80 mt-1">
            {data.bonus_earned ? `Every story and brand stamp collected — your +${data.bonus_points} bonus points have been added.` : "Every story and brand stamp collected!"}
          </p>
        </div>
      )}
      {!data.complete && data.bonus_points > 0 && (
        <p className="text-[10px] text-white/40 mb-3 tracking-wide">Collect all {data.total + (data.brands_total || 0)} stamps — story stamps from quizzes, brand stamps from ordering — to unlock a +{data.bonus_points} point bonus.</p>
      )}
      <div className="grid grid-cols-3 gap-3">
        {data.stamps.map((s) => (
          <Link key={s.slug} to={`/experience/dish/${s.slug}`} data-testid={`story-stamp-${s.slug}`}
            className={`relative rounded-2xl border p-3 flex flex-col items-center gap-2 transition-colors ${
              s.collected ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#121212] hover:border-[#D4AF37]/30"}`}>
            {s.collected && (
              <button data-testid={`story-stamp-share-${s.slug}`} aria-label={`Share your ${s.dish_name} stamp`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); shareStamp({ place: s.stamp_place, year: s.stamp_year, dishName: s.dish_name }); }}
                className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/50 border border-[#D4AF37]/40 text-[#D4AF37]">
                <Share2 size={11} />
              </button>
            )}
            <div className={`w-20 h-20 ${s.collected ? "" : "opacity-40"}`}>
              <StampSeal place={s.stamp_place} year={s.stamp_year} dim={!s.collected} />
            </div>
            <p className={`text-[9px] font-bold tracking-widest text-center leading-tight ${s.collected ? "text-[#D4AF37]" : "text-white/45"}`}>
              {s.dish_name.replace(/\s*\(.*?\)/, "").split("—")[0].trim().toUpperCase()}
            </p>
            {!s.collected && <span className="text-[8px] tracking-[0.2em] text-white/35">TAP TO PLAY</span>}
          </Link>
        ))}
        {(data.brand_stamps || []).map((b) => (
          <Link key={b.brand} to={`/experience/brands/${b.brand}`} data-testid={`brand-stamp-${b.brand}`}
            className={`relative rounded-2xl border p-3 flex flex-col items-center gap-2 transition-colors ${
              b.collected ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#121212] hover:border-[#D4AF37]/30"}`}>
            {b.collected && (
              <button data-testid={`brand-stamp-share-${b.brand}`} aria-label={`Share your ${b.name} stamp`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); shareStamp({ place: b.name.toUpperCase(), year: "2026", dishName: b.title }); }}
                className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/50 border border-[#D4AF37]/40 text-[#D4AF37]">
                <Share2 size={11} />
              </button>
            )}
            <div className={`w-20 h-20 ${b.collected ? "" : "opacity-40"}`}>
              <StampSeal place={b.name.toUpperCase()} year="2026" dim={!b.collected} />
            </div>
            <p className={`text-[9px] font-bold tracking-widest text-center leading-tight ${b.collected ? "text-[#D4AF37]" : "text-white/45"}`}>
              {b.title}
            </p>
            {!b.collected && (
              <span data-testid={`brand-stamp-status-${b.brand}`} className="text-[8px] tracking-[0.2em] text-white/35">
                {b.coming_soon ? "COMING SOON" : "ORDER TO EARN"}
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};

const ChampionsBoard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/quiz/champions", { params: { user_id: USER_ID, table: localStorage.getItem("xp_table") || "" } })
      .then((r) => {
        setData(r.data);
        const c = r.data.last_week_champion;
        if (c?.is_winner && c.code && sessionStorage.getItem(`xp_champ_${c.code}`) !== "1") {
          sessionStorage.setItem(`xp_champ_${c.code}`, "1");
          window.dispatchEvent(new CustomEvent("xp-mascot", { detail: { type: "champion" } }));
        }
      }).catch(() => {});
  }, []);
  if (!data || (data.board.length === 0 && !data.last_week_champion)) return null;
  const champ = data.last_week_champion;
  return (
    <div className="mt-8" data-testid="champions-board">
      <SectionTitle kicker="Taste & Expert — this week" title="Quiz champions" testId="section-champions" />
      {champ && (
        <div data-testid="champion-banner" className={`mb-3 rounded-2xl border p-4 ${champ.is_winner ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-[#121212]"}`}>
          <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-bold">LAST WEEK'S CHAMPION</p>
          <p className="text-sm font-black mt-1">{champ.winner}</p>
          <p className="text-xs text-white/60 mt-0.5">Wins: {champ.prize}</p>
          {champ.is_winner && champ.code && (
            <p data-testid="champion-code" className="mt-2 text-xs font-bold text-[#D4AF37]">
              YOUR PRIZE CODE — SHOW YOUR SERVER: <span className="font-display tracking-[0.15em]">{champ.code}</span>
            </p>
          )}
        </div>
      )}
      {data.board.length > 0 && (
      <div className="rounded-2xl bg-[#121212] border border-white/10 divide-y divide-white/5">
        {data.board.map((row, i) => (
          <div key={row.name} data-testid={`champion-row-${i}`}
            className={`flex items-center gap-3 px-4 py-3 ${row.is_you ? "bg-[#D4AF37]/10" : ""}`}>
            <span className={`w-6 text-center font-display text-lg font-bold ${i === 0 ? "text-[#D4AF37]" : "text-white/40"}`}>{i + 1}</span>
            <span className="flex-1 text-xs font-semibold tracking-wide truncate">{row.name}{row.is_you ? " · YOU" : ""}</span>
            <span className="text-[10px] text-white/40 shrink-0">{row.correct} correct</span>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [placing, setPlacing] = useState("");
  useEffect(() => {
    api.get(`/checkout/history?user_id=${USER_ID}`).then((r) => setOrders(r.data)).catch(() => {});
  }, []);
  if (orders.length === 0) return null;

  const reorder = async (o) => {
    setPlacing(o.code);
    try {
      const r = await api.post("/checkout/handoff", {
        user_id: USER_ID, fulfillment: o.fulfillment,
        items: o.items.map(({ slug, name, price, qty, brand }) => ({ slug, name, price, qty, brand })),
      });
      track("checkout_reorder", { code: r.data.code, source: "passport_history" });
      window.open(r.data.url, "_blank", "noopener");
      toast.success("Cart sent to checkout — finish your prepaid order in the tab that just opened", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reorder — please try again");
    }
    setPlacing("");
  };

  return (
    <div className="mt-8" data-testid="section-order-history">
      <SectionTitle kicker="Past paid orders — one tap to run it back" title="Order history" testId="section-order-history-title" />
      <div className="rounded-2xl bg-[#121212] border border-white/10 divide-y divide-white/5">
        {orders.map((o) => (
          <div key={o.code} data-testid={`order-history-row-${o.code}`} className="px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-black tracking-widest text-[#D4AF37] uppercase">
                {o.fulfillment} · {String(o.paid_at).slice(0, 10)} · #{o.code}
              </p>
              <span className="text-sm font-black text-[#D4AF37] shrink-0">{money(o.total)}</span>
            </div>
            <p className="text-[11px] text-white/55 mt-1 line-clamp-2">
              {o.items.map((i) => `${i.name} ×${i.qty}`).join(" · ")}
            </p>
            <button data-testid={`order-history-reorder-${o.code}`} onClick={() => reorder(o)} disabled={!!placing}
              className="mt-2.5 w-full flex items-center justify-center gap-1.5 border border-[#D4AF37]/50 text-[#D4AF37] font-bold rounded-full py-2.5 text-[10px] tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-60">
              <RotateCcw size={12} /> {placing === o.code ? "OPENING CHECKOUT…" : "REORDER IN ONE TAP"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

function PassportInner() {
  useSeo({ title: "My Urban Passport", noindex: true, path: "/experience/passport" });
  const flags = useFlags();
  const t = useT();
  const [data, setData] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [challengeAnswers, setChallengeAnswers] = useState({});
  const [showAllLedger, setShowAllLedger] = useState(false);

  const load = useCallback(() => {
    api.get(`/passport/${USER_ID}`).then((r) => setData(r.data));
    api.get("/journeys", { params: { user_id: USER_ID } }).then((r) => setJourneys(r.data));
    api.get("/challenges", { params: { user_id: USER_ID } }).then((r) => setChallenges(r.data));
  }, []);
  useEffect(load, [load]);

  if (!data) return <div className="p-6 text-white/50 text-sm">Opening your passport…</div>;

  const { user, level, achievements, favorites, ledger } = data;
  const nextMin = level.next_level?.min_points || user.lifetime_points;
  const prevMin = data.levels.find((l) => l.name === level.name)?.min_points || 0;
  const pct = level.next_level ? Math.min(100, Math.round(((user.lifetime_points - prevMin) / (nextMin - prevMin)) * 100)) : 100;
  const visibleLedger = showAllLedger ? ledger : ledger.slice(0, 6);

  const completeChallenge = async (c) => {
    const r = await api.post(`/challenges/${c.slug}/complete`, { user_id: USER_ID, answer: challengeAnswers[c.slug] });
    rewardToast(r.data);
    if (!r.data.awarded && r.data.message) {
      const { toast } = await import("sonner");
      toast(r.data.message);
    }
    load();
  };

  return (
    <div data-testid="passport-page" className="px-4 pt-6 pb-12 mx-auto w-full max-w-3xl">
      {/* POINTS HERO */}
      <Fade>
        <section data-testid="passport-hero" className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-[#141414] p-6 text-center">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-44 rounded-full bg-[#D4AF37]/[0.12] blur-3xl pointer-events-none" />
          <p className="text-[10px] tracking-[0.4em] text-[#D4AF37] font-bold">URBAN PASSPORT</p>
          <p className="text-sm text-white/50 mt-1" data-testid="passport-name">{user.name}</p>
          {/* points number hidden from customers (June 2026) — data still stored */}
          <span data-testid="passport-level" className="inline-flex items-center gap-1.5 mt-4 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5 text-[11px] font-bold tracking-widest text-[#D4AF37]">
            <Crown size={12} /> LEVEL {level.level} · {level.name.toUpperCase()}
          </span>
          <div className="mt-5">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.9, ease: "easeOut" }} className="h-full bg-gradient-to-r from-[#B5A642] to-[#F0C742] rounded-full" />
            </div>
            <p className="text-[11px] text-white/50 mt-2">
              {level.next_level ? `Next: ${level.next_level.name}` : "Maximum level reached"}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-5 text-center">
            <div className="rounded-xl bg-black/40 border border-white/5 py-3"><p className="text-lg font-black text-white">{data.dishes_tried}</p><p className="text-[9px] tracking-widest text-white/40">DISHES</p></div>
            <div className="rounded-xl bg-black/40 border border-white/5 py-3"><p className="text-lg font-black text-white">{data.punjab_explored_pct}%</p><p className="text-[9px] tracking-widest text-white/40">PUNJAB</p></div>
            <div className="rounded-xl bg-black/40 border border-white/5 py-3" data-testid="passport-streak"><p className="text-lg font-black text-[#E25822]">🔥{data.streak?.days || 0}</p><p className="text-[9px] tracking-widest text-white/40">STREAK</p></div>
          </div>
        </section>
      </Fade>

      {/* POINTS EXPIRY WARNING — hidden with the points display (June 2026) */}
      {false && data.points_expiry?.expiring_soon && (
        <div data-testid="points-expiry-warning" className="mt-4 rounded-2xl border border-[#E25822]/50 bg-[#E25822]/10 p-4">
          <p className="text-xs font-semibold text-[#E25822]">
            {`${user.points} PTS · ${t("passport.expiring")}`}
          </p>
        </div>
      )}

      {/* SHARED PHONE IDENTITY ACROSS ALL BRANDS */}
      <PhoneLink user={user} onLinked={load} />

      {/* REWARD WALL */}
      <Fade delay={0.08}>
        <RewardWall onRedeemed={load} />
      </Fade>

      {/* PERSONALITY */}
      <Fade delay={0.12}>
        <Link to="/experience/quiz" data-testid="passport-quiz-link" className="mt-4 block rounded-2xl bg-[#121212] border border-white/10 p-4 hover:border-[#D4AF37]/40 transition-colors">
          <div className="flex items-center gap-3">
            <Brain size={18} className="text-[#D4AF37]" />
            <div>
              <p className="text-sm font-semibold">{user.personality ? `You are ${user.personality}` : "What Punjabi dish are you?"}</p>
              <p className="text-[11px] text-white/50">{user.personality ? "Retake the quiz anytime" : "Take the 5-question food personality quiz · +10 pts"}</p>
            </div>
          </div>
        </Link>
      </Fade>

      {/* CHAMPIONS */}
      <Fade delay={0.14}>
        <ChampionsBoard />
      </Fade>

      {/* STORY STAMPS */}
      <Fade delay={0.15}>
        <StoryStamps />
      </Fade>

      {/* CHALLENGES */}
      {flagOn(flags, "challenges") && (
        <Fade delay={0.16}>
          <div className="mt-8">
            <SectionTitle kicker="Daily challenges" title="Today at Urban" testId="section-challenges" />
            <div className="space-y-3">
              {challenges.map((c) => (
                <div key={c.slug} data-testid={`challenge-${c.slug}`} className={`rounded-2xl border p-5 ${c.completed ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-[#D4AF37]/20 bg-[#121212]"}`}>
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-semibold flex-1">{c.title}</p>
                  </div>
                  <p className="text-[11px] text-white/50 mt-1">{c.description}</p>
                  {c.fact && <p className="text-[11px] text-white/70 mt-2 border-l-2 border-[#D4AF37]/40 pl-2">{c.fact}</p>}
                  {!c.completed && c.q && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-semibold">{c.q}</p>
                      {c.options.map((o, i) => (
                        <button key={i} data-testid={`challenge-${c.slug}-option-${i}`}
                          onClick={() => setChallengeAnswers({ ...challengeAnswers, [c.slug]: i })}
                          className={`w-full text-left rounded-lg border px-3 py-2 text-xs transition-colors ${challengeAnswers[c.slug] === i ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/25"}`}>
                          {o}
                        </button>
                      ))}
                    </div>
                  )}
                  {!c.completed && (
                    <button data-testid={`challenge-${c.slug}-submit`} onClick={() => completeChallenge(c)}
                      className="mt-3 bg-[#D4AF37] text-black text-xs font-bold rounded-full px-4 py-2 tracking-widest hover:bg-[#F0C742] transition-colors">
                      {c.q ? "SUBMIT" : "CHECK PROGRESS"}
                    </button>
                  )}
                  {c.completed && <p className="text-[11px] text-[#D4AF37] mt-2 font-bold">✓ COMPLETED</p>}
                </div>
              ))}
            </div>
          </div>
        </Fade>
      )}

      {/* ACHIEVEMENTS */}
      {flagOn(flags, "achievements") && (
        <Fade delay={0.2}>
          <div className="mt-8">
            <SectionTitle kicker="Badges · tap an unlocked badge to share" title="Achievements" testId="section-achievements" />
            <div className="grid grid-cols-4 gap-3">
              {achievements.map((a) => {
                const Icon = ICONS[a.icon] || Star;
                return (
                  <button key={a.slug} data-testid={`achievement-${a.slug}`} title={`${a.name} — ${a.requirement}`}
                    onClick={() => a.unlocked && shareBadge(a.name, a.description, "ACHIEVEMENT UNLOCKED")}
                    className={`relative rounded-2xl border p-3 flex flex-col items-center gap-1.5 text-center transition-colors ${a.unlocked ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 shadow-[0_0_16px_rgba(212,175,55,0.12)]" : "border-white/10 bg-[#141414] opacity-40"}`}>
                    {a.unlocked ? <Icon size={18} className="text-[#D4AF37]" /> : <Lock size={16} className="text-white/40" />}
                    <p className="text-[8px] font-bold tracking-wide leading-tight">{a.name}</p>
                    {a.unlocked && <Share2 size={9} className="absolute top-1.5 right-1.5 text-[#D4AF37]/70" />}
                  </button>
                );
              })}
            </div>
          </div>
        </Fade>
      )}

      {/* JOURNEYS — vertical timeline */}
      {flagOn(flags, "journeys") && (
        <Fade delay={0.24}>
          <div className="mt-8">
            <SectionTitle kicker="Food journeys" title="Your expeditions" testId="section-journeys" />
            <div className="relative pl-6">
              <div className="absolute left-[7px] top-3 bottom-6 w-px bg-white/10" />
              {journeys.map((j) => (
                <Link key={j.slug} to={`/experience/journey/${j.slug}`} data-testid={`journey-card-${j.slug}`}
                  className="relative block mb-3">
                  <span className={`absolute -left-6 top-5 w-[15px] h-[15px] rounded-full border-2 ${j.completed ? "bg-[#D4AF37] border-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.5)]" : j.progress > 0 ? "bg-[#141414] border-[#D4AF37]/60" : "bg-[#141414] border-white/25"}`} />
                  <div className="rounded-2xl bg-[#121212] border border-white/10 p-4 hover:border-[#D4AF37]/40 transition-colors">
                    <div className="flex justify-between items-center">
                      <p className="font-display text-lg font-semibold">{j.name}</p>
                      <span className="text-[10px] text-[#D4AF37] font-bold">{j.completed ? "COMPLETE" : `${j.progress}%`}</span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#D4AF37] rounded-full" style={{ width: `${j.progress}%` }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Fade>
      )}

      {/* FAVORITES */}
      {favorites.length > 0 && (
        <div className="mt-8">
          <SectionTitle kicker="My favorites" title="Saved dishes" testId="section-favorites" />
          <div className="flex gap-3 overflow-x-auto snap-x hide-scrollbar">
            {favorites.map((d) => <DishCard key={d.slug} dish={d} compact />)}
          </div>
        </div>
      )}

      {/* ORDER HISTORY */}
      <OrderHistory />

      {/* RECENT ACTIVITY */}
      <div className="mt-8">
        <SectionTitle kicker="Your activity" title="Recent activity" testId="section-ledger" />
        <div className="rounded-2xl bg-[#121212] border border-white/10 divide-y divide-white/5">
          {visibleLedger.map((t) => (
            <div key={t.transaction_id} className="flex justify-between items-center px-5 py-3">
              <div>
                <p className="text-xs font-semibold capitalize">{t.type}</p>
                <p className="text-[10px] text-white/40">{t.reason}</p>
              </div>
            </div>
          ))}
          {ledger.length === 0 && <p className="px-5 py-4 text-xs text-white/50">Explore dishes, play challenges and earn your first Urban Points.</p>}
          {ledger.length > 6 && (
            <button data-testid="ledger-toggle" onClick={() => setShowAllLedger(!showAllLedger)}
              className="w-full py-3 text-[11px] font-bold tracking-widest text-[#D4AF37] flex items-center justify-center gap-1.5">
              {showAllLedger ? "SHOW LESS" : `SHOW ALL ${ledger.length}`}
              <ChevronDown size={13} className={`transition-transform ${showAllLedger ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
